import moneyMask from '@eduzz/houston-forms/masks/money';
import Input from '@eduzz/houston-ui/Forms/Input';
import Typography from '@eduzz/houston-ui/Typography';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Link from '@material-ui/core/Link';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Tooltip from '@material-ui/core/Tooltip';
import CancelIcon from '@material-ui/icons/Cancel';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';
import { tap } from 'rxjs/operators';
import Modal from 'v2/components/Shared/Modal';
import { sendEventToGA } from 'v2/helpers/analytics';
import { numberToBRL } from 'v2/helpers/money';
import { anticipationService } from 'v2/services/anticipation';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import * as Yup from 'yup';

import type { requestAnticipation } from 'v2/services/anticipation';

import styles from './styles';

import type { Props, AnticipationOptions, AnticipationByValue } from './constants';

function AnticipationModal({
  anticipationData,
  isOpen,
  setClose,
  updateTransferAvailable,
  handleToggleTransferModal,
  handleUpdateAvailableAmount,
  handleOpenSuggestionAnticipation,
  loadSuggestionAnticipation,
  handleOpenFidcModal,
  hasSuggestionAnticipation,
}: Props) {
  const classes = styles();

  const [showSuccess, setShowSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingByValue, setIsLoadingByValue] = useState(false);

  const [valueToAnticipate, setValueToAnticipate] = useState(null);
  const [valueToAnticipateByValue, setValueToAnticipateByValue] = useState<null | AnticipationByValue>(null);
  const [anticipateOptionSelected, setAnticipateOptionSelected] = useState('');

  const [formIsValid, setFormIsValid] = useState(true);
  const [formMessageError, setFormMessageError] = useState(null);

  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);
  const [currentBalance] = useObservable(() => anticipationService.getCurrentBalance(), []);
  const [totalInvoices] = useObservable(() => anticipationService.getTotalInvoices(), []);

  useObservable(() => anticipationService.loadData(), []);

  const [requestAnticipation] = useCallbackObservable((anticipation: requestAnticipation) => {
    setIsLoading(true);

    return anticipationService.requestAnticipation(anticipation).pipe(
      tap(
        () => {
          setIsLoading(false);
          setShowSuccess(true);
          updateTransferAvailable();
          handleUpdateAvailableAmount();
        },
        (err) => {
          toast.error(err);
          setIsLoading(false);
        }
      )
    );
  }, []);

  const [getAvailableByValue] = useCallbackObservable((requestedValue: number) => {
    setIsLoadingByValue(true);
    return anticipationService.getAvailableByValue(requestedValue).pipe(
      tap(({ data }) => setValueToAnticipateByValue(data)),
      tap(() => setIsLoadingByValue(false))
    );
  }, []);

  const nothingToAnticipate = !currentBalance && !showSuccess;
  const valueRequestItsTotal = currentBalance === valueToAnticipate;
  const hasAnticipationOptions = !!valueToAnticipateByValue;
  const somethingIsLoading = isLoading || isLoadingByValue;
  const anticipationOptionNotSelected = !valueRequestItsTotal && hasAnticipationOptions && !anticipateOptionSelected;
  const valueRequestedItsExact = !!valueToAnticipateByValue?.request_value;
  const requestedorTotalInvoices = valueToAnticipateByValue?.request_value?.invoices || totalInvoices;

  const balanceFormatted = typeof currentBalance === 'number' ? numberToBRL(currentBalance) : '-';
  const currentFee = anticipationData?.has_promotional_fee ? `${anticipationData?.custom_fee?.value}%` : '2.99%';

  const validateValue = async (value: string) => {
    const anticipateSchema = Yup.object().shape({
      valueToAnticipate: Yup.number()
        .required()
        .min(0.01, `Valor mínimo: ${numberToBRL(0.01)}`)
        .max(currentBalance, `Valor máximo: ${balanceFormatted}`),
    });

    try {
      await anticipateSchema.validate({ valueToAnticipate: value });
      setFormIsValid(true);
      setFormMessageError(null);
    } catch (err) {
      setFormIsValid(false);
      setFormMessageError(err.message);
    }
  };

  const submitRequest = () => {
    const anticipationDataRequest = {
      amount_requested: currentBalance,
      total_invoices: totalInvoices,
    };

    if (anticipateOptionSelected) {
      sendEventToGA({
        category: 'Novo Extrato',
        action: 'Antecipação_Parcial',
        label: `Sugestão_${anticipateOptionSelected === 'low_value' ? 'Menor' : 'Maior'}_Valor`,
      });

      anticipationDataRequest.amount_requested = valueToAnticipateByValue[anticipateOptionSelected].value;
      anticipationDataRequest.total_invoices = valueToAnticipateByValue[anticipateOptionSelected].invoices;
    }

    if (valueRequestedItsExact) {
      anticipationDataRequest.amount_requested = valueToAnticipateByValue.request_value.value;
      anticipationDataRequest.total_invoices = valueToAnticipateByValue.request_value.invoices;
    }

    loadSuggestionAnticipation();
    requestAnticipation(anticipationDataRequest);
  };

  const handleOpenTransferModal = () => {
    const showGalaxyEligibleAlert = user?.eligible;
    if (showGalaxyEligibleAlert) {
      handleOpenFidcModal();
      return;
    }

    handleToggleTransferModal();
  };

  const handleGoToTranfer = () => {
    setIsLoading(true);
    setTimeout(() => {
      updateTransferAvailable();
      handleUpdateAvailableAmount();
      setIsLoading(false);
      handleOpenTransferModal();
      setClose();
    }, 3000);
  };

  const getCopyNextStepButton = () => {
    if (somethingIsLoading) return 'Carregando...';
    if (showSuccess) return 'Realizar transferência';
    if (!valueRequestItsTotal && !hasAnticipationOptions) return 'Verificar';
    return 'Confirmar antecipação';
  };

  const handleCloseSuccessAnticipation = () => {
    setClose();
    if (hasSuggestionAnticipation) {
      handleOpenSuggestionAnticipation();
    }
  };

  const getFunctionNextStepButton = () => {
    if (!valueRequestItsTotal && !hasAnticipationOptions) return handleGetAvailableByValue();
    if (!showSuccess) return submitRequest();
    return handleGoToTranfer();
  };

  const shouldDisableNextStepButton = () => {
    return nothingToAnticipate || !formIsValid || (anticipationOptionNotSelected && !valueRequestedItsExact) || somethingIsLoading;
  };

  const handleInputChange = (value: string) => {
    setValueToAnticipateByValue(null);
    setAnticipateOptionSelected('');
    validateValue(value);
    setValueToAnticipate(value);
  };

  const handleInputReset = () => {
    setValueToAnticipateByValue(null);
    setAnticipateOptionSelected('');
    setValueToAnticipate('');
    validateValue('0');
  };

  const handleChangeAnticipationOptions = (event: React.ChangeEvent<HTMLInputElement>) => setAnticipateOptionSelected(event.target.value);

  const handleGetAvailableByValue = () => {
    sendEventToGA({
      category: 'Novo Extrato',
      action: 'Antecipação_Parcial',
      label: 'Verificar',
    });

    getAvailableByValue(valueToAnticipate);
  };

  const getAnticipationOption = (props: AnticipationOptions, optionValue: string) => {
    if (!props) return <></>;

    return (
      <FormControlLabel
        value={optionValue}
        control={<Radio />}
        className={classes.anticipationOption}
        label={
          <>
            <big>
              <strong>{numberToBRL(props.value)}</strong>
            </big>
            <span>
              <strong>Tarifa:</strong> {numberToBRL(props.fee)}
            </span>
            <span>
              <strong>Faturas:</strong> {props.invoices}
            </span>
          </>
        }
      />
    );
  };

  useEffect(() => {
    !!currentBalance && setValueToAnticipate(currentBalance);
  }, [currentBalance]);

  return (
    <Modal
      open={isOpen}
      size='xs'
      setClose={setClose}
      textTitle={!showSuccess ? 'Antecipar saldo' : 'Parabéns!'}
      textReturnButton='Agora não'
      textProceedButton={getCopyNextStepButton()}
      handleProceedClick={getFunctionNextStepButton}
      handleReturnClick={!showSuccess ? setClose : handleCloseSuccessAnticipation}
      isProceedButtonDisabled={shouldDisableNextStepButton()}
    >
      {!showSuccess && (
        <>
          <Typography size='xxs' marginBottom>
            Saldo disponível para antecipação:
          </Typography>
          <Typography color='primary' size='sm' weight='semibold' marginBottom>
            {balanceFormatted}
          </Typography>
          <Typography size='xxs' marginBottom>
            Digite o valor desejado:
          </Typography>
          <Input
            className={classes.inputValue}
            name='valueToAnticipate'
            placeholder='R$ 0,00'
            disabled={nothingToAnticipate || somethingIsLoading}
            value={valueToAnticipate}
            onChange={handleInputChange}
            mask={moneyMask}
            errorMessage={!formIsValid && formMessageError}
            endAdornment={<CancelIcon className={classes.clearValue} onClick={handleInputReset} />}
          />

          {(valueRequestItsTotal || valueRequestedItsExact) && (
            <small className={classes.description}>{`Esse valor é referente a ${requestedorTotalInvoices} fatura${
              requestedorTotalInvoices > 1 ? 's' : ''
            } antecipáve${requestedorTotalInvoices > 1 ? 'is' : 'l'}.`}</small>
          )}

          {!valueRequestItsTotal && !valueRequestedItsExact && valueToAnticipateByValue && (
            <>
              <div className={classes.optionsDescription}>
                <span>Escolha o melhor valor para suas necessidades:</span>
                <Tooltip title='Os valores sugeridos foram obtidos das faturas antecipáveis (da venda mais antiga para a mais nova).' placement='top'>
                  <HelpOutlineIcon fontSize='small' />
                </Tooltip>
              </div>

              <RadioGroup value={anticipateOptionSelected} onChange={handleChangeAnticipationOptions}>
                <div className={classes.optionsList}>
                  {getAnticipationOption(valueToAnticipateByValue?.low_value, 'low_value')}
                  {getAnticipationOption(valueToAnticipateByValue?.up_value, 'up_value')}
                </div>
              </RadioGroup>
            </>
          )}

          <div className={classes.description}>
            <span>O recebimento antes do prazo incorre tarifa administrativa de {currentFee} + 0.1% por dia antecipado. </span>
            <Link
              href='https://ajuda.eduzz.com/hc/pt-br/articles/4402057357211-Como-funciona-a-antecipa%C3%A7%C3%A3o-%C3%93rbita-'
              rel='noopener noreferrer'
              target='_blank'
            >
              Saiba mais
            </Link>
          </div>
        </>
      )}

      {showSuccess && (
        <div className={classes.successMsg}>
          <img width={64} height={63} src='/img/success_icon.svg' alt='Ícone verde de sucesso' />
          <span>Parabéns, sua solitação de antecipação de valores foi efetuada com sucesso!</span>
          <strong>A lista de faturas antecipadas será enviada para o seu e-mail assim que a solicitação for processada.</strong>
          <strong>Em alguns instantes seu saldo disponível estará atualizado.</strong>
        </div>
      )}
    </Modal>
  );
}

export default AnticipationModal;
